import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import QRCode from "qrcode";
import { g, p, del, pu, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { QrCode, RefreshCw, Power, Copy, MessageCircle, Smartphone } from "lucide-react";

/** Ingresso del personale: si inquadra il QR sullo schermo del titolare, niente codici da dettare. */
export const AccessoStaff = () => {
  const { user, settings, refresh } = useApp();
  const [d, setD] = useState(null);
  const [qr, setQr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    g("/tenant/staff-link").then(setD).catch(() => setD(null));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (d?.url) QRCode.toDataURL(d.url, { margin: 1, width: 420 }).then(setQr);
    else setQr("");
  }, [d]);

  if (!d) return null;

  const azione = async (fn, msg) => {
    setBusy(true);
    try {
      setD(await fn());
      toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const testo = `Ingresso personale di ${settings?.business_name || "il locale"}: apri questo link sul telefono, tocca il tuo nome e inserisci il tuo PIN. ${d.url}`;

  return (
    <Card className="space-y-4 p-6" data-testid="accesso-staff-card">
      <div className="flex items-start gap-3">
        <QrCode className="mt-0.5 h-5 w-5 shrink-0 text-slate-700" />
        <div>
          <h3 className="font-semibold">Come entra il personale</h3>
          <p className="mt-1 text-sm text-slate-500">
            Il tuo staff non deve conoscere il codice del locale. Fai inquadrare questo QR dal loro
            telefono (o mandagli il link): si apre l'elenco dei nomi e il tastierino. Per entrare serve
            comunque il PIN personale, quindi il QR da solo non fa entrare nessuno.
          </p>
        </div>
      </div>

      {d.active ? (
        <>
          <div className="flex flex-col items-center gap-3 rounded-xl bg-slate-50 p-4 sm:flex-row sm:items-start">
            {qr && <img src={qr} alt="QR ingresso personale" className="h-40 w-40 rounded-lg bg-white p-2" data-testid="accesso-staff-qr" />}
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Il tuo link</p>
              <p className="mt-1 break-all text-xs text-slate-600" data-testid="accesso-staff-url">{d.url}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" data-testid="accesso-staff-copy"
                        onClick={() => { navigator.clipboard.writeText(d.url); toast.success("Link copiato"); }}>
                  <Copy className="mr-1 h-3.5 w-3.5" /> Copia il link
                </Button>
                <Button asChild size="sm" variant="outline" data-testid="accesso-staff-wa">
                  <a href={`https://wa.me/?text=${encodeURIComponent(testo)}`} target="_blank" rel="noreferrer">
                    <MessageCircle className="mr-1 h-3.5 w-3.5" /> WhatsApp
                  </a>
                </Button>
                <Button asChild size="sm" variant="outline" data-testid="accesso-staff-sms">
                  <a href={`sms:?&body=${encodeURIComponent(testo)}`}>
                    <Smartphone className="mr-1 h-3.5 w-3.5" /> SMS
                  </a>
                </Button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" disabled={busy} data-testid="accesso-staff-nuovo"
                    onClick={() => {
                      if (!window.confirm("Faccio un codice nuovo? Il QR e il link di prima smettono subito di funzionare: chi li aveva salvati dovrà rifare l'ingresso.")) return;
                      azione(() => p("/tenant/staff-link", { by: user?.name || "Titolare" }), "Codice nuovo pronto: il vecchio non vale più");
                    }}>
              <RefreshCw className="mr-1 h-3.5 w-3.5" /> Fai un codice nuovo
            </Button>
            <Button size="sm" variant="outline" className="border-rose-300 text-rose-700" disabled={busy}
                    data-testid="accesso-staff-off"
                    onClick={() => {
                      if (!window.confirm("Spengo l'ingresso rapido? Il personale tornerà a inserire nome attività e codice.")) return;
                      azione(() => del("/tenant/staff-link"), "Ingresso rapido disattivato");
                    }}>
              <Power className="mr-1 h-3.5 w-3.5" /> Disattiva
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            <b>Quando qualcuno se ne va</b>: togli la spunta «attivo» alla persona in questa pagina (il suo
            PIN non funziona più) e, se ha salvato il link, tocca «Fai un codice nuovo» e rimanda il QR
            agli altri. Due tocchi, nessuna carta.
          </p>

          <label className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 p-3">
            <span className="min-w-0">
              <span className="block text-sm font-semibold">Calendario turni</span>
              <span className="block text-xs text-slate-500">
                Serve solo se ti è utile: accendendolo hai una griglia con la settimana di ogni persona
                (riposo oppure orario), che si ripete e si può correggere per un singolo giorno. La vedono
                tutti, la cambia solo la direzione. Se lo lasci spento, resta l'orario unico qui sotto.
              </span>
            </span>
            <Switch checked={!!settings?.shifts_on} data-testid="shifts-on-switch"
                    onCheckedChange={async (v) => {
                      try {
                        await pu("/settings", { ...settings, cover_charge: Number(settings.cover_charge) || 0, shifts_on: v });
                        await refresh();
                        toast.success(v ? "Calendario turni attivo: lo trovi nel menu «Turni»" : "Calendario turni spento");
                      } catch (e) {
                        toast.error(errMsg(e));
                      }
                    }} />
          </label>

          <label className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 p-3">
            <span className="min-w-0">
              <span className="block text-sm font-semibold">Entrano solo nel proprio turno</span>
              <span className="block text-xs text-slate-500">
                Con questa spunta chi ha un orario di turno indicato qui sotto non riesce a entrare fuori
                da quell'orario (con 20 minuti di tolleranza prima e dopo). Chi è in ferie, malattia o
                riposo è già bloccato in automatico. Titolare e direttore entrano sempre.
              </span>
            </span>
            <Switch checked={!!settings?.shift_lock} data-testid="shift-lock-switch"
                    onCheckedChange={async (v) => {
                      try {
                        await pu("/settings", { ...settings, cover_charge: Number(settings.cover_charge) || 0, shift_lock: v });
                        await refresh();
                        toast.success(v ? "Accesso consentito solo in orario di turno" : "Accesso libero a ogni ora");
                      } catch (e) {
                        toast.error(errMsg(e));
                      }
                    }} />
          </label>
        </>
      ) : (
        <div className="rounded-xl bg-slate-50 p-4">
          <p className="text-sm text-slate-600">
            L'ingresso rapido è spento: il personale deve inserire nome attività e codice ogni volta su un
            telefono nuovo.
          </p>
          <Button className="mt-3 h-11" disabled={busy} data-testid="accesso-staff-attiva"
                  onClick={() => azione(() => p("/tenant/staff-link", { by: user?.name || "Titolare" }), "Ingresso del personale attivato")}>
            <QrCode className="mr-1.5 h-4 w-4" /> Attiva l'ingresso col QR
          </Button>
        </div>
      )}
    </Card>
  );
};
